/**
 * YouTube links
 * -------------
 * Pasting a YouTube link runs the same pipeline as uploading a file from the
 * computer. The only difference is how the video reaches storage: instead of
 * the browser uploading it, the server downloads it straight from YouTube —
 * usually much faster than an upload from home — and streams its progress
 * back here, so the upload dock can show it like upload progress.
 */
import axios from "axios";
import { API_BASE_URL } from "../../api";

const YT_ID = /^[A-Za-z0-9_-]{11}$/;
const YT_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "music.youtube.com",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
  "youtu.be",
  "www.youtu.be",
]);

const FAILED_MESSAGE =
  "We couldn't download this YouTube video. Please try again, or upload the file instead.";
const POLL_INTERVAL_MS = 4000;
const REATTACH_EVERY_POLLS = 8; // ~30s
const MAX_POLLS = (2 * 60 * 60 * 1000) / POLL_INTERVAL_MS; // give up after ~2h

export class YouTubeLinkError extends Error {
  constructor(code, message) {
    super(message || FAILED_MESSAGE);
    this.name = "YouTubeLinkError";
    this.code = code || "FAILED";
  }
}

/**
 * The video id from any common YouTube link (watch, youtu.be, shorts, live,
 * embed, mobile, music) as { id, url }, or null. Mirrors the server's parser.
 */
export function parseYouTubeLink(input) {
  if (typeof input !== "string") return null;
  let raw = input.trim();
  if (!raw || raw.length > 2048) return null;
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) raw = `https://${raw}`;

  let url;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (!["http:", "https:"].includes(url.protocol)) return null;

  const host = url.hostname.toLowerCase();
  if (!YT_HOSTS.has(host)) return null;

  let id = null;
  if (host.endsWith("youtu.be")) {
    id = url.pathname.split("/")[1];
  } else if (url.pathname === "/watch" || url.pathname === "/watch/") {
    id = url.searchParams.get("v");
  } else {
    const match = url.pathname.match(/^\/(?:shorts|live|embed|v|e)\/([^/]+)/);
    if (match) id = match[1];
  }

  if (!id || !YT_ID.test(id)) return null;
  return { id, url: `https://www.youtube.com/watch?v=${id}` };
}

/** True for text that looks like a link someone pasted (YouTube or not). */
export const looksLikeLink = (text) =>
  /^(https?:\/\/|www\.)|^[\w-]+(\.[\w-]+)+\//i.test(String(text || "").trim());

const isCanceled = (error) =>
  error?.code === "ERR_CANCELED" || error?.name === "CanceledError";

function errorFromResponse(status, data = {}) {
  // The record is gone: a failed import is cleaned up like a failed upload.
  if (status === 404) return new YouTubeLinkError("FAILED");
  if (status === 401) {
    return new YouTubeLinkError("AUTH", "Log in to add videos from YouTube.");
  }
  if (status === 429) {
    return new YouTubeLinkError(
      "RATE_LIMITED",
      "Too many requests — please wait a moment and try again."
    );
  }
  return new YouTubeLinkError(data.code, data.message || data.error);
}

/**
 * Title, duration and thumbnail for a pasted link — shown before anything is
 * downloaded. The server also starts resolving the video right away, so the
 * download can begin as soon as the user confirms.
 */
export async function fetchYouTubePreview(url, { signal } = {}) {
  try {
    const { data } = await axios.post(
      `${API_BASE_URL}/videos/api/fetch_youtube`,
      { url },
      { withCredentials: true, timeout: 90000, signal }
    );
    return data;
  } catch (error) {
    if (isCanceled(error)) throw error;
    if (!error.response) {
      throw new YouTubeLinkError(
        "NETWORK",
        "Couldn't reach our servers. Check your connection and try again."
      );
    }
    const err = errorFromResponse(error.response.status, error.response.data);
    if (!error.response.data?.message && err.code === "FAILED") {
      err.message =
        "Couldn't fetch that YouTube link. Please check the URL and try again.";
    }
    throw err;
  }
}

// Plain dark 16:9 JPEG: the last-resort thumbnail, so an upload never fails
// over a missing one.
const PLACEHOLDER_JPEG =
  "data:image/jpeg;base64,/9j/4AAQSkZJRgABAgAAAQABAAD//gAPTGF2YzYxLjMuMTAwAP/bAEMACBAQExATFhYWFhYWGhgaGxsbGhoaGhsbGx0dHSIiIh0dHRsbHR0gICIiJSYlIyMiIyYmKCgoMDAuLjg4OkVFU//EAE0AAQEAAAAAAAAAAAAAAAAAAAAHAQEBAQAAAAAAAAAAAAAAAAAAAwQQAQAAAAAAAAAAAAAAAAAAAAARAQAAAAAAAAAAAAAAAAAAAAD/wAARCABaAKADASIAAhEAAxEA/9oADAMBAAIRAxEAPwCTgNKIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD//2Q==";

async function blobFrom(src) {
  const res = await fetch(src);
  if (!res.ok) throw new Error(`thumbnail ${res.status}`);
  const blob = await res.blob();
  if (!blob.size) throw new Error("empty thumbnail");
  return blob;
}

/**
 * The preview's thumbnail as a Blob, playing the same role as the frame
 * grabbed from a local file. The server sends it inline (no CORS issues);
 * the image URL is only a fallback.
 */
export async function thumbnailBlobFor(preview) {
  const sources = [preview?.thumbnailDataUrl, preview?.thumbnailUrl, PLACEHOLDER_JPEG];
  for (const src of sources.filter(Boolean)) {
    try {
      return await blobFrom(src);
    } catch {
      // try the next source
    }
  }
  return new Blob([], { type: "image/jpeg" });
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function handleLine(line, onProgress) {
  if (!line) return null;
  let event;
  try {
    event = JSON.parse(line);
  } catch {
    return null;
  }
  if (event.type === "progress") onProgress(event);
  if (["done", "error", "detached"].includes(event.type)) return event;
  return null;
}

// Reads the server's newline-delimited JSON progress stream until it reports
// an outcome; { type: "dropped" } if the connection ended without one.
async function readProgressStream(response, onProgress) {
  if (!response.body?.getReader) {
    const text = await response.text().catch(() => "");
    for (const line of text.split("\n")) {
      const outcome = handleLine(line.trim(), onProgress);
      if (outcome) return outcome;
    }
    return { type: "dropped" };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let newline;
      while ((newline = buffer.indexOf("\n")) >= 0) {
        const outcome = handleLine(buffer.slice(0, newline).trim(), onProgress);
        buffer = buffer.slice(newline + 1);
        if (outcome) {
          reader.cancel().catch(() => {});
          return outcome;
        }
      }
    }
    return handleLine(buffer.trim(), onProgress) || { type: "dropped" };
  } catch {
    return { type: "dropped" };
  }
}

async function postImport(videoId, onProgress) {
  let response;
  try {
    response = await fetch(`${API_BASE_URL}/videos/api/import_youtube/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ video_id: videoId }),
    });
  } catch {
    return { type: "dropped" };
  }
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw errorFromResponse(response.status, data);
  }
  return readProgressStream(response, onProgress);
}

async function fetchImportStatus(videoId) {
  try {
    const { data } = await axios.get(
      `${API_BASE_URL}/videos/api/import_status/${videoId}`,
      { withCredentials: true, timeout: 15000 }
    );
    return data;
  } catch (error) {
    const status = error.response?.status;
    if (status === 401 || status === 404) {
      throw errorFromResponse(status, error.response.data);
    }
    return null; // transient — keep following
  }
}

/**
 * Have the server download the YouTube video behind `videoId` (a record from
 * create_video) into storage. Resolves once it's stored — the caller then
 * carries on exactly like after a finished local upload. `onProgress` gets
 * { stage, percent, speedBps, etaSeconds, waitSeconds }.
 *
 * Progress arrives live on the import request. If that connection drops (a
 * proxy or the server's request timeout) — or another server instance is
 * doing the download — we follow the import by polling and periodically
 * re-attach, which also restarts an import whose server instance went away.
 */
export async function importYouTubeVideo(videoId, { onProgress = () => {} } = {}) {
  let polls = 0;
  for (;;) {
    const outcome = await postImport(videoId, onProgress);
    if (outcome.type === "done") return;
    if (outcome.type === "error") {
      throw new YouTubeLinkError(outcome.code, outcome.message);
    }

    for (let i = 0; i < REATTACH_EVERY_POLLS; i += 1) {
      if (++polls > MAX_POLLS) {
        throw new YouTubeLinkError(
          "TIMEOUT",
          "Downloading from YouTube took too long. Please try again, or upload the file instead."
        );
      }
      await sleep(POLL_INTERVAL_MS);
      const data = await fetchImportStatus(videoId);
      if (!data) continue;
      if (data.status === "done") return;
      if (data.status === "error") throw new YouTubeLinkError(data.code, data.message);
      onProgress(data);
    }
  }
}
